import React, { useEffect, useMemo, useState } from 'react';
import { siteSettingsAPI } from '../../services/api';
import {
  LEGACY_MAINTENANCE_SETTING,
  MAINTENANCE_PAGES,
  MAINTENANCE_PAGES_SETTING,
  createMaintenanceState,
  parseMaintenanceState,
  serializeMaintenanceState
} from '../../config/maintenancePages';
import './AdminComponents.css';
import './MaintenanceManagement.css';

const SETTINGS_KEYS = `${MAINTENANCE_PAGES_SETTING},${LEGACY_MAINTENANCE_SETTING}`;

const MaintenanceManagement = () => {
  const [pageStates, setPageStates] = useState(() => createMaintenanceState(false));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await siteSettingsAPI.get(SETTINGS_KEYS);
        if (response.success && response.data) {
          setPageStates(parseMaintenanceState(
            response.data[MAINTENANCE_PAGES_SETTING],
            response.data[LEGACY_MAINTENANCE_SETTING]
          ));
        }
      } catch (error) {
        console.error('Error loading maintenance settings:', error);
        setMessage(error.message || 'Failed to load maintenance settings.');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const enabledCount = useMemo(
    () => MAINTENANCE_PAGES.filter((page) => pageStates[page.id]).length,
    [pageStates]
  );
  const allEnabled = enabledCount === MAINTENANCE_PAGES.length;

  const togglePage = (pageId) => {
    setMessage('');
    setPageStates((current) => ({
      ...current,
      [pageId]: !current[pageId]
    }));
  };

  const toggleAll = () => {
    setMessage('');
    setPageStates(createMaintenanceState(!allEnabled));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setMessage('');

      const payload = {
        [MAINTENANCE_PAGES_SETTING]: serializeMaintenanceState(pageStates),
        [LEGACY_MAINTENANCE_SETTING]: allEnabled ? 'true' : 'false'
      };
      const response = await siteSettingsAPI.update(payload);
      const savedSettings = response.success && response.data ? response.data : payload;

      window.dispatchEvent(new CustomEvent('siteSettingsUpdated', {
        detail: savedSettings
      }));
      setMessage('Maintenance settings saved.');
    } catch (error) {
      console.error('Error saving maintenance settings:', error);
      setMessage(error.message || 'Failed to save maintenance settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-section">
        <div className="loading-message">Loading maintenance settings...</div>
      </div>
    );
  }

  return (
    <div className="admin-section maintenance-management">
      <div className="admin-section-header maintenance-admin-header">
        <div>
          <h2 className="admin-section-title">Maintenance</h2>
          <p className="maintenance-admin-intro">
            Choose exactly which public pages display the maintenance screen. Admin access always remains available.
          </p>
        </div>
        <div className="admin-section-actions maintenance-admin-actions">
          <button
            type="button"
            className="admin-button"
            onClick={toggleAll}
            disabled={saving}
          >
            Toggle All
          </button>
          <button
            type="button"
            className="admin-button primary"
            onClick={saveSettings}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="maintenance-admin-summary" aria-live="polite">
        <span className={`maintenance-admin-status ${enabledCount ? 'active' : ''}`}>
          {enabledCount ? 'Maintenance Active' : 'All Pages Live'}
        </span>
        <span>{enabledCount} of {MAINTENANCE_PAGES.length} pages in maintenance</span>
      </div>

      {message && (
        <div className="maintenance-admin-message" role="status">
          {message}
        </div>
      )}

      <div className="maintenance-admin-list">
        {MAINTENANCE_PAGES.map((page) => {
          const enabled = Boolean(pageStates[page.id]);
          return (
            <div className="maintenance-admin-row" key={page.id}>
              <div className="maintenance-admin-page-copy">
                <strong>{page.label}</strong>
                <span>{page.description}</span>
              </div>
              <div className="maintenance-admin-row-control">
                <span className={enabled ? 'maintenance-state-active' : 'maintenance-state-live'}>
                  {enabled ? 'Maintenance' : 'Live'}
                </span>
                <button
                  type="button"
                  className={`maintenance-switch ${enabled ? 'active' : ''}`}
                  role="switch"
                  aria-checked={enabled}
                  aria-label={`Toggle maintenance for ${page.label}`}
                  onClick={() => togglePage(page.id)}
                  disabled={saving}
                >
                  <span className="maintenance-switch-thumb" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MaintenanceManagement;
